import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Plus, Search, LifeBuoy } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canCasesAuthor, useAuth } from "@/lib/auth";
import { CASES_V2 } from "@/constants/testIds";

const STATUS_COLORS = {
  PENDING_RECEPTION:            "bg-slate-100 text-slate-700",
  WITH_WAREHOUSE:               "bg-indigo-100 text-indigo-800",
  WITH_QC:                      "bg-violet-100 text-violet-800",
  WITH_BATTERY:                 "bg-fuchsia-100 text-fuchsia-800",
  WITH_RECEPTION_FOR_DISPATCH:  "bg-amber-100 text-amber-800",
  SHIPPED:                      "bg-sky-100 text-sky-800",
  DELIVERED:                    "bg-emerald-100 text-emerald-800",
  CLOSED:                       "bg-emerald-100 text-emerald-800",
  NON_REPAIRABLE:               "bg-rose-100 text-rose-800",
  WARRANTY_REJECTED:            "bg-rose-100 text-rose-800",
  // legacy V1 statuses still supported for old rows
  OPEN: "bg-slate-100 text-slate-700",
  ASSIGNED: "bg-sky-100 text-sky-800",
  INSPECTION: "bg-indigo-100 text-indigo-800",
  AWAITING_CUSTOMER: "bg-amber-100 text-amber-800",
  APPROVED: "bg-teal-100 text-teal-800",
  REJECTED: "bg-rose-100 text-rose-800",
  REPAIR: "bg-violet-100 text-violet-800",
  REPLACEMENT: "bg-fuchsia-100 text-fuchsia-800",
};

const STATUS_OPTIONS = [
  "PENDING_RECEPTION", "WITH_WAREHOUSE", "WITH_QC", "WITH_BATTERY",
  "WITH_RECEPTION_FOR_DISPATCH", "SHIPPED", "DELIVERED", "CLOSED",
  "NON_REPAIRABLE", "WARRANTY_REJECTED",
];

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

export default function CasesV2Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => ({
    limit: 100, offset: 0,
    q: q || undefined, status: status || undefined,
  }), [q, status]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/cases-v2", { params });
      setRows(data.items || []);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load cases"));
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid={CASES_V2.page}>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Customer Cases</h1>
          <p className="text-sm text-slate-500 mt-1">
            V2 workflow — Reception → Warehouse / QC → Battery → Dispatch. All cases (new and legacy) appear here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid={CASES_V2.refreshBtn} onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          {canCasesAuthor(user) && (
            <button type="button" data-testid={CASES_V2.newBtn}
              onClick={() => navigate("/admin/cases/new")}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
              <Plus size={14} /> New Case
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 shadow-sm">
        <div className="sm:col-span-3 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid={CASES_V2.searchInput}
            placeholder="Case No · Customer · Mobile · Brand · Model · Serial"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid={CASES_V2.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={CASES_V2.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Case No.</th>
              <th className="text-left px-4 py-3">Opened</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Laptop</th>
              <th className="text-left px-4 py-3">Serial</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Warranty</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Prints</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400">
                <LifeBuoy size={24} className="inline-block mb-2 text-slate-300" />
                <div>No cases match your filters.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={CASES_V2.listRow(r.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/admin/cases/${r.id}`)}>
                <td className="px-4 py-3 font-mono text-xs text-slate-900">{r.case_no}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {r.opened_at ? new Date(r.opened_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-medium">{r.customer_name || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{r.mobile || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="text-slate-800">{[r.brand, r.model].filter(Boolean).join(" ") || "—"}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.manufacturer_serial || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {(r.customer_source || "").replaceAll("_", " ") || "—"}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {r.warranty_days == null
                    ? "Contract"
                    : r.warranty_days === 0
                    ? "Paid"
                    : `${r.warranty_days}d${r.warranty_end_date ? ` · ${r.warranty_end_date}` : ""}`}
                </td>
                <td className="px-4 py-3"><Chip text={String(r.status || "").replaceAll("_", " ")} cls={STATUS_COLORS[r.status]} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.print_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
